import React, { useState, useEffect, useCallback } from "react";
import { CreditCard, CheckCircle, Loader2, Sparkles } from "lucide-react";
import { API_URL, axios } from "../../constants";
import { getCustomerToken, isCustomerLoggedIn } from "../../utils/customerAuth";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import { logger } from "../../utils/logger";
import confetti from "canvas-confetti";
import AddOnPopup from "../AddOnPopup";
import { BookingSuccess } from "./BookingSuccess";
import { BookingCalendar } from "./BookingCalendar";
import BookingFormFields from "./BookingFormFields";
import { BookingAddressSelector } from "./BookingAddressSelector";
import { playBookingConfirmed } from "../../utils/sounds";
import { PriceSummary, PaymentOptions, DiscountSection, PaymentBadges, LoginPromptModal, BookingConfirmedModal } from "./BookingFormParts";
import { getBreakdown, getPayableAmount, getDiscountAmount as calcDiscountAmount } from "../../utils/bookingPricing";

const fireConfetti = () => {
  const colors = ['#E83E8C', '#FF9F1C', '#FFD166', '#25D366'];
  confetti({ particleCount: 50, spread: 80, origin: { y: 0.6 }, colors, gravity: 1.2 });
  setTimeout(() => {
    confetti({ particleCount: 15, angle: 60, spread: 40, origin: { x: 0.1, y: 0.6 }, colors });
    confetti({ particleCount: 15, angle: 120, spread: 40, origin: { x: 0.9, y: 0.6 }, colors });
  }, 400);
};

export const OnlineBookingSection = ({ capturedCustomer }) => {
  const { requireAuth } = useCustomerAuth();
  const [formData, setFormData] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    date: '', setup_time: '', package: '', occasion: 'Birthday', occasion_name: '',
    pincode: '', address: '', location_lat: null, location_lng: null,
    payment_type: 'advance', notes: '', referral_code: '', coupon_code: '',
    whatsapp_consent: true
  });
  const [loading, setLoading] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [bookingCounts, setBookingCounts] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [showAddOns, setShowAddOns] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [isCustomerLoggedIn, setIsCustomerLoggedIn] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [pendingAddOns, setPendingAddOns] = useState(null);
  const [pincodeStatus, setPincodeStatus] = useState(null); // {serviceable, message, area, city}
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [formRestored, setFormRestored] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null); // null = "add new"
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [earlyBird, setEarlyBird] = useState(null); // {active, slots_remaining, total_slots, discount_inr, label}
  const [magicDesign, setMagicDesign] = useState(null); // DoozyGo Magic → Book Online design reference

  // Fetch Early Bird promo state — DISABLED (iter164 founder kill-switch).
  // The strip + badge + Rs-1000-off line were still rendering on prod. Keeping
  // the fetch skipped means `earlyBird` stays null → no badge, no "Launch
  // offer" line in BookingFormParts, and earlyBirdDiscount stays 0 (no
  // pricing surprise). Flip DISABLE_EARLY_BIRD to false to resume promo.
  const DISABLE_EARLY_BIRD = true;
  useEffect(() => {
    if (DISABLE_EARLY_BIRD) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/api/promo/early-bird`).then(res => {
      if (cancelled) return;
      setEarlyBird(res.data);
      if (res.data?.active && res.data?.slots_remaining > 0) {
        axios.post(`${API_URL}/api/promo/early-bird/event`, {
          kind: "impression",
          surface: "badge",
          page: window.location.pathname || "/",
        }).catch(() => { /* fire and forget */ });
      }
    }).catch(() => { /* silent — promo simply won't show */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch saved addresses when logged in
  useEffect(() => {
    const token = getCustomerToken();
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API_URL}/api/customer-auth/addresses`, { withCredentials: true, headers })
      .then(res => {
        const addrs = res.data.addresses || [];
        setSavedAddresses(addrs);
        // Auto-select default address if form address is empty
        if (addrs.length > 0 && !formData.address) {
          const def = addrs.find(a => a.is_default) || addrs[0];
          setSelectedAddressId(def.address_id);
          setFormData(prev => ({ ...prev, address: def.address, pincode: def.pincode || prev.pincode }));
        } else if (addrs.length === 0) {
          setShowNewAddressForm(true);
        }
      })
      .catch(err => logger.error('[Booking] Address fetch error:', err.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomerLoggedIn]);

  // Check customer auth status on mount AND prefill name/email/phone/address from profile
  // Single source of truth: /api/customer-auth/me returns name + email + phone + last_address.
  // Without this, logged-in customers see a blank form on every visit (the "memory" bug).
  useEffect(() => {
    const token = getCustomerToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API_URL}/api/customer-auth/me`, { withCredentials: true, headers })
      .then(res => {
        setIsCustomerLoggedIn(true);
        const d = res.data || {};
        setFormData(prev => ({
          ...prev,
          customer_name: prev.customer_name || d.name || "",
          customer_email: prev.customer_email || d.email || "",
          customer_phone: prev.customer_phone || d.phone || "",
          // Address prefilled from last booking; user can still pick a different saved address below.
          address: prev.address || d.last_address || "",
        }));
      })
      .catch(() => setIsCustomerLoggedIn(false));
  }, []);

  // Restore form data from localStorage (after login redirect)
  useEffect(() => {
    const saved = localStorage.getItem('doozygo_booking_form');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Expire after 2 hours
        if (parsed._ts && Date.now() - parsed._ts > 2 * 60 * 60 * 1000) {
          localStorage.removeItem('doozygo_booking_form');
          localStorage.removeItem('doozygo_pending_addons');
          return;
        }
        const { _ts, ...formFields } = parsed;
        setFormData(prev => ({ ...prev, ...formFields }));
        setFormRestored(true);
        // Restore add-ons
        const savedAddOns = localStorage.getItem('doozygo_pending_addons');
        if (savedAddOns) {
          const addOns = JSON.parse(savedAddOns);
          setSelectedAddOns(addOns);
          setPendingAddOns(addOns);
        }
        // Scroll to booking form
        setTimeout(() => {
          document.getElementById('book-online')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 500);
      } catch (err) { logger.error('[Booking] Auth check error:', err.message); }
    }
  }, []);


  useEffect(() => {
    if (capturedCustomer) {
      setFormData(prev => ({
        ...prev,
        customer_name: prev.customer_name || capturedCustomer.name || '',
        customer_phone: prev.customer_phone || capturedCustomer.phone || '',
        customer_email: prev.customer_email || capturedCustomer.email || ''
      }));
    }
  }, [capturedCustomer]);

  // DoozyGo Magic → Book Online: load the server-side design draft by opaque id
  // (no PII in URL, ownership enforced server-side). Falls back to the cached
  // design on refresh / back-navigation so the selected look is never lost.
  useEffect(() => {
    const applyDesign = (d) => {
      if (!d) return;
      setMagicDesign(d);
      setFormData(prev => ({
        ...prev,
        package: d.package || prev.package,
        occasion: d.occasion || prev.occasion,
      }));
      setTimeout(() => {
        document.getElementById('book-online')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    };
    const params = new URLSearchParams(window.location.search);
    const draftId = params.get('draft');
    if (draftId) {
      const token = getCustomerToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      axios.get(`${API_URL}/api/customer-auth/booking-draft/${draftId}`, { withCredentials: true, headers })
        .then(res => {
          const d = res.data || {};
          applyDesign(d);
          try { localStorage.setItem('doozygo_magic_design', JSON.stringify(d)); } catch { /* ignore */ }
        })
        .catch(err => logger.error('[Booking] Draft load error:', err.message));
    } else {
      try {
        const cached = localStorage.getItem('doozygo_magic_design');
        if (cached) applyDesign(JSON.parse(cached));
      } catch { /* ignore */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const occasions = ['Birthday', 'Anniversary', 'Baby Shower', 'Housewarming', 'Engagement', 'Retirement', 'Other'];
  const packagePrices = { Basic: 3999, Classic: 5999, Premium: 8999, Luxury: 14999, Custom: 0 };
  const ADVANCE_AMOUNT = 1000;

  const fetchAvailability = useCallback(async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await axios.get(`${API_URL}/api/availability/${year}/${month}`);
      setBookedDates(response.data.booked_dates || []);
      setBookingCounts(response.data.booking_counts || {});
    } catch (err) { logger.error("Failed to fetch availability:", err); }
  }, [currentMonth]);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  // Listen for package selection from landing page "Book Now" buttons
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) {
        // Convert UPPERCASE tag (e.g. "BASIC") to Title Case (e.g. "Basic") to match dropdown
        const tag = e.detail;
        const pkg = tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
        setFormData(prev => ({ ...prev, package: pkg }));
      }
    };
    window.addEventListener('selectPackage', handler);
    return () => window.removeEventListener('selectPackage', handler);
  }, []);

  const addOnsTotal = selectedAddOns.reduce((sum, a) => sum + a.price * a.quantity, 0);

  // Pincode validation with debounce
  useEffect(() => {
    const pin = formData.pincode.trim();
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
      setPincodeStatus(null);
      return;
    }
    setPincodeLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API_URL}/api/pincode/validate`, { pincode: pin });
        setPincodeStatus(res.data);
      } catch {
        setPincodeStatus({ serviceable: false, message: "Unable to verify pincode" });
      } finally {
        setPincodeLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.pincode]);

  // Pricing breakdown: Package price is inclusive of GST (18%) and EMF (15%)
  // Formula: GST = 18% of package, Remaining = 82% of package, EMF = 15% of Remaining, Decoration = 85% of Remaining
  // Early-bird discount is only applied to FULL payment (advance is fixed; savings
  // honored on balance at setup). Test mode never gets the discount.
  const earlyBirdDiscount = (earlyBird && earlyBird.active && formData.payment_type === 'full')
    ? Number(earlyBird.discount_inr || 0)
    : 0;

  const getAmount = () => getPayableAmount({
    packagePrice: packagePrices[formData.package],
    addOnsTotal,
    paymentType: formData.payment_type,
    advanceAmount: ADVANCE_AMOUNT,
    referralValid,
    couponValid,
    couponDiscount,
    earlyBirdDiscount,
  });

  const getDiscountAmount = () => calcDiscountAmount({
    packagePrice: packagePrices[formData.package],
    addOnsTotal,
    referralValid,
    couponValid,
    couponDiscount,
  });

  const validateReferralCode = async (code) => {
    if (!code || code.length < 5) { setReferralValid(null); setReferralMessage(''); return; }
    try {
      const res = await axios.post(`${API_URL}/api/referral/validate`, { code: code.trim().toUpperCase() });
      setReferralValid(true); setReferralMessage(res.data.message);
      // Clear coupon if referral is valid
      setCouponValid(null); setCouponMessage(''); setCouponDiscount(null);
      setFormData(prev => ({ ...prev, coupon_code: '' }));
    } catch (err) {
      setReferralValid(false); setReferralMessage(err.response?.data?.detail || 'Invalid code');
    }
  };

  const validateCouponCode = async (code) => {
    if (!code || code.length < 3) { setCouponValid(null); setCouponMessage(''); setCouponDiscount(null); return; }
    try {
      const res = await axios.post(`${API_URL}/api/coupon/validate`, { code: code.trim().toUpperCase() });
      setCouponValid(true); setCouponMessage(res.data.message); setCouponDiscount(res.data);
      // Clear referral if coupon is valid
      setReferralValid(null); setReferralMessage('');
      setFormData(prev => ({ ...prev, referral_code: '' }));
    } catch (err) {
      setCouponValid(false); setCouponMessage(err.response?.data?.detail || 'Invalid coupon');
      setCouponDiscount(null);
    }
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    // Centralised login gate — customers must be signed in to begin booking.
    if (!isCustomerLoggedIn()) {
      // Persist the form so a Google-login redirect can restore it on return.
      try {
        localStorage.setItem('doozygo_booking_form', JSON.stringify({ ...formData, _ts: Date.now() }));
        localStorage.setItem('doozygo_return_url', '/#book-online');
      } catch { /* storage disabled */ }
      requireAuth(() => handlePreSubmit(e));
      return;
    }
    if (!formData.date) { setError('Please select a date'); return; }
    if (!formData.setup_time) { setError('Please select a setup time'); return; }
    if (!formData.package) { setError('Please select a package'); return; }
    if (formData.occasion === 'Other' && !formData.occasion_name.trim()) { setError('Please tell us what the party is for'); return; }
    if (!formData.address || formData.address.trim().length < 5) { setError('Please enter your full address (or pick it on the map)'); return; }
    // Pincode is only required when the map pin didn't resolve a coordinate.
    // When we have an exact lat/lng, serviceability is checked via pincode but
    // pincode absence alone shouldn't block submission — field crews navigate
    // via the pin.
    if (!formData.pincode || formData.pincode.length !== 6) { setError('Please enter a valid 6-digit pincode (or search your address on the map above to auto-fill it)'); return; }
    if (!pincodeStatus || !pincodeStatus.serviceable) { setError('Sorry, we don\'t serve this pincode yet. Please check our serviceable areas.'); return; }
    // If add-ons were restored from a previous session, skip popup and go straight to payment
    if (formRestored && selectedAddOns.length > 0) {
      setFormRestored(false);
      handleAddOnsConfirm(selectedAddOns);
      return;
    }
    setShowAddOns(true);
  };

  const handleAddOnsConfirm = (addOns) => {
    // For Custom package, require at least one add-on with min Rs 999 total
    if (formData.package === 'Custom') {
      const total = addOns.reduce((sum, a) => sum + a.price * a.quantity, 0);
      if (addOns.length === 0 || total < 999) {
        setError('Custom package requires at least Rs 999 worth of selections. Please pick your party elements!');
        setShowAddOns(false);
        return;
      }
    }
    setSelectedAddOns(addOns);
    setShowAddOns(false);

    // Check if customer is logged in before proceeding to payment
    // Also do a quick re-check via localStorage token (handles post-redirect race)
    let loggedIn = isCustomerLoggedIn;
    if (!loggedIn) {
    const token = getCustomerToken();
    if (token) { loggedIn = true; setIsCustomerLoggedIn(true); }
    }
    if (!loggedIn) {
      setPendingAddOns(addOns);
      localStorage.setItem('doozygo_pending_addons', JSON.stringify(addOns));
      setShowLoginPrompt(true);
      return;
    }
    doSubmit(addOns);
  };

  const handleLoginRedirect = () => {
    // Save form data to localStorage (survives OAuth redirect chain reliably)
    localStorage.setItem('doozygo_booking_form', JSON.stringify({ ...formData, _ts: Date.now() }));
    localStorage.setItem('doozygo_return_url', '/#book-online');
    sessionStorage.setItem('doozygo_return_to_booking', 'true');
    window.location.href = '/my-account?returnTo=booking';
  };

  const doSubmit = async (addOns) => {
    setLoading(true); setError('');
    try {
      const payload = { ...formData, add_ons: addOns };
      // DoozyGo Magic origin: carry source + AI design reference to CRM/decorator.
      if (magicDesign) {
        payload.booking_source = 'DOOZYGO_MAGIC';
        payload.magic_job_id = magicDesign.magic_job_id || null;
        payload.design_image_url = magicDesign.design_image_url || null;
        payload.design_theme = magicDesign.theme || null;
        payload.design_colors = magicDesign.colors || null;
      }
      // If occasion is "Other", merge the custom name
      if (formData.occasion === 'Other' && formData.occasion_name.trim()) {
        payload.occasion = `Other - ${formData.occasion_name.trim()}`;
      }
      // Creator Leaderboard: attach referral code if the visitor came via /r/CODE
      try {
        const creatorRef = localStorage.getItem('dg_creator_ref') ||
          (document.cookie.match(/(?:^|;\s*)dg_creator_ref=([^;]+)/) || [])[1];
        if (creatorRef) payload.creator_ref_code = creatorRef;
      } catch { /* ignore */ }
      const response = await axios.post(`${API_URL}/api/bookings/create-order`, payload);
      if (response.data.key_id && response.data.order_id !== 'offline') {
        const options = {
          key: response.data.key_id, amount: response.data.amount, currency: response.data.currency,
          name: 'DoozyGo®\u00ae', description: `${formData.package} Package - ${formData.occasion}`,
          order_id: response.data.order_id,
          handler: async (paymentResponse) => {
            try {
              await axios.post(`${API_URL}/api/bookings/verify-payment`, {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
                booking_id: response.data.booking_id,
                creator_ref_code: payload.creator_ref_code || ""
              });
              // Referral credit: if this user visited someone's invite, award the host ₹500 credits
              const fp = localStorage.getItem('dg_fp');
              if (fp) {
                axios.post(`${API_URL}/api/smart-invites/referrals/track-booking`, {
                  fingerprint: fp, phone: formData.customer_phone || null
                }).catch(() => {});
              }
              // Clean up saved form data after successful booking
              localStorage.removeItem('doozygo_booking_form');
              localStorage.removeItem('doozygo_pending_addons');
              localStorage.removeItem('doozygo_return_url');
              localStorage.removeItem('doozygo_magic_design');
              fireConfetti();
              playBookingConfirmed();
              setConfirmedBooking({
                package: formData.package, date: formData.date, setup_time: formData.setup_time,
                occasion: formData.occasion === 'Other' && formData.occasion_name.trim() ? `Other - ${formData.occasion_name.trim()}` : formData.occasion,
                name: formData.customer_name, booking_id: response.data.booking_id
              });
            } catch (err) { setError('Payment verification failed. Please contact support.'); }
          },
          prefill: { name: formData.customer_name, email: formData.customer_email, contact: formData.customer_phone },
          theme: { color: '#E83E8C' }
        };
        const rzp = new window.Razorpay(options); rzp.open();
      } else {
        setSuccess(true);
        setTimeout(() => {
          document.getElementById('book-online')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          fireConfetti();
          playBookingConfirmed();
        }, 200);
      }
    } catch (err) { setError(err.response?.data?.detail || 'Failed to create booking. Please try again.'); }
    finally { setLoading(false); }
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear(); const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1); const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    const today = new Date();
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const count = bookingCounts[i] || 0;
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({ day: i, isPast, isFullyBooked: count >= 5, hasOneBooking: count >= 3, dateStr });
    }
    return days;
  };

  const getDateColor = (dayInfo, isSelected) => {
    if (isSelected) return 'bg-[#E83E8C] text-white ring-2 ring-[#E83E8C] ring-offset-1 sm:ring-offset-2';
    if (dayInfo.isPast) return 'text-gray-300 cursor-not-allowed';
    if (dayInfo.isFullyBooked) return 'bg-red-500 text-white cursor-not-allowed';
    if (dayInfo.hasOneBooking) return 'bg-orange-100 text-orange-700 hover:bg-orange-200 cursor-pointer';
    return 'bg-green-50 text-green-700 hover:bg-[#E83E8C] hover:text-white cursor-pointer';
  };

  const pkgPrice = packagePrices[formData.package] || 0;

  if (success) {
    return <BookingSuccess onReset={() => { setSuccess(false); setFormData(prev => ({ ...prev, date: '' })); setSelectedAddOns([]); }} />;
  }

  return (
    <>
    <section id="book-online" className="py-20 md:py-28 bg-gradient-to-br from-[#E83E8C]/5 via-white to-[#FF9F1C]/5" data-testid="booking-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#E83E8C]/10 text-[#E83E8C] px-4 py-2 rounded-full mb-4"><CreditCard size={18} /><span className="text-sm font-semibold">Secure Online Booking</span></div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-4">Book Your Party Online</h2>
          <p className="text-[#475569] text-lg">Pay advance or full amount securely with UPI, Cards & more</p>
          {earlyBird && earlyBird.active && earlyBird.slots_remaining > 0 && (
            <div className="mt-5 inline-flex items-center gap-2.5 bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-300/60 rounded-full pl-2 pr-4 py-1.5 shadow-sm" data-testid="early-bird-badge">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-400 text-white text-[11px] font-bold">★</span>
              <span className="text-[13px] font-semibold text-[#92400E]">
                Launch offer · ₹{Number(earlyBird.discount_inr).toLocaleString('en-IN')} OFF
              </span>
              <span className="text-[12px] text-amber-700/80">
                · only <span className="font-bold">{earlyBird.slots_remaining}</span> of {earlyBird.total_slots} left
              </span>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calendar */}
          <BookingCalendar
            currentMonth={currentMonth} setCurrentMonth={setCurrentMonth}
            selectedDate={formData.date}
            onSelectDate={(dateStr) => setFormData({ ...formData, date: dateStr })}
            getDaysInMonth={getDaysInMonth} getDateColor={getDateColor}
          />

          {/* Booking Form */}
          <form onSubmit={handlePreSubmit} className="bg-white rounded-3xl shadow-xl p-4 sm:p-6 space-y-4" data-testid="booking-form">
            {formRestored && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm flex items-center justify-between" data-testid="form-restored-banner">
                <span className="flex items-center gap-2"><CheckCircle size={16} /> Your booking details have been restored. Review and continue!</span>
                <button type="button" onClick={() => setFormRestored(false)} className="text-green-400 hover:text-green-600"><span className="text-lg leading-none">&times;</span></button>
              </div>
            )}
            {error && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">{error}</div>}
            <BookingFormFields
              formData={formData}
              setFormData={setFormData}
              occasions={occasions}
            />

            {/* ── YOUR SELECTED DESIGN (DoozyGo Magic → Book Online) ── */}
            {magicDesign && (magicDesign.design_image || magicDesign.package) && (
              <div className="rounded-2xl border border-[#E83E8C]/20 bg-gradient-to-br from-[#E83E8C]/5 via-white to-[#8b5cf6]/5 overflow-hidden" data-testid="booking-selected-design">
                <div className="px-4 py-2.5 border-b border-[#E83E8C]/10 flex items-center gap-2">
                  <Sparkles size={15} className="text-[#E83E8C]" />
                  <span className="text-xs font-bold tracking-wide text-[#1E1B4B] uppercase">Your Selected Design</span>
                </div>
                <div className="p-4 flex gap-3">
                  {magicDesign.design_image ? (
                    <img src={magicDesign.design_image} alt="Your AI decoration design" className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover border border-[#f0e6d9] flex-shrink-0" data-testid="booking-design-image" />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {magicDesign.package && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#E83E8C]/10 text-[#E83E8C] text-[11px] font-semibold" data-testid="booking-design-package">{magicDesign.package}</span>
                      )}
                      {magicDesign.occasion && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#8b5cf6]/10 text-[#8b5cf6] text-[11px] font-semibold" data-testid="booking-design-occasion">{magicDesign.occasion}</span>
                      )}
                    </div>
                    {(magicDesign.colors || magicDesign.theme) && (
                      <p className="text-[11px] text-[#475569] leading-snug">
                        {magicDesign.theme && <span>Theme: <b>{magicDesign.theme}</b>. </span>}
                        {magicDesign.colors && <span>Colours: <b>{magicDesign.colors}</b></span>}
                      </p>
                    )}
                    <p className="text-[10px] text-[#94a3b8] mt-1.5 inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF9F1C]" /> AI-Generated Design Reference
                    </p>
                    <p className="text-[10px] text-[#94a3b8] mt-1 leading-snug">A visual reference for our decorator &amp; ops team. Exact elements may vary from your package inclusions.</p>
                  </div>
                </div>
              </div>
            )}

            <BookingAddressSelector
              formData={formData}
              setFormData={setFormData}
              isCustomerLoggedIn={isCustomerLoggedIn}
              savedAddresses={savedAddresses}
              selectedAddressId={selectedAddressId}
              setSelectedAddressId={setSelectedAddressId}
              showNewAddressForm={showNewAddressForm}
              setShowNewAddressForm={setShowNewAddressForm}
              pincodeStatus={pincodeStatus}
              setPincodeStatus={setPincodeStatus}
              pincodeLoading={pincodeLoading}
            />

            {/* Selected Add-ons Summary */}
            {selectedAddOns.length > 0 && (
              <div className="bg-[#E83E8C]/5 rounded-xl p-3 border border-[#E83E8C]/10" data-testid="addons-summary">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-[#E83E8C]">Add-ons Selected</p>
                  <button type="button" onClick={() => setShowAddOns(true)} className="text-xs text-[#E83E8C] underline">Edit</button>
                </div>
                {selectedAddOns.map(a => (
                  <div key={a.id} className="flex justify-between text-xs text-[#475569]">
                    <span>{a.name} x{a.quantity}</span>
                    <span>Rs {(a.price * a.quantity).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between text-xs font-semibold text-[#1E1B4B] mt-1 pt-1 border-t border-[#E83E8C]/10">
                  <span>Add-ons Total</span>
                  <span>Rs {addOnsTotal.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Discount Codes Section */}
            <DiscountSection
              formData={formData} setFormData={setFormData}
              referralValid={referralValid} referralMessage={referralMessage}
              couponValid={couponValid} couponMessage={couponMessage}
              validateReferralCode={validateReferralCode} validateCouponCode={validateCouponCode}
            />

            {/* Payment Options */}
            <PaymentOptions formData={formData} setFormData={setFormData} ADVANCE_AMOUNT={ADVANCE_AMOUNT} pkgPrice={pkgPrice} addOnsTotal={addOnsTotal} />

            {/* Price Summary */}
            <PriceSummary
              pkgPrice={pkgPrice} addOnsTotal={addOnsTotal}
              getBreakdown={getBreakdown} getDiscountAmount={getDiscountAmount} getAmount={getAmount}
              referralValid={referralValid} couponValid={couponValid} couponDiscount={couponDiscount} formData={formData}
              earlyBird={earlyBird} earlyBirdDiscount={earlyBirdDiscount}
            />

            {/* WhatsApp opt-in (DPDP-compliant; default opted-in for India where WA is the default channel) */}
            <label className="flex items-start gap-2 px-3 py-2.5 bg-[#25D366]/5 border border-[#25D366]/20 rounded-xl cursor-pointer text-xs text-[#1E1B4B] leading-snug" data-testid="whatsapp-consent-row">
              <input
                type="checkbox"
                checked={formData.whatsapp_consent !== false}
                onChange={(e) => setFormData({ ...formData, whatsapp_consent: e.target.checked })}
                className="mt-0.5 accent-[#25D366]"
                data-testid="whatsapp-consent-checkbox"
              />
              <span>
                <b>Send setup updates on WhatsApp</b> — booking confirmation, day-before reminder, and live tracking link.
                You can opt out anytime; we'll switch to email & SMS instead.
              </span>
            </label>

            <button type="submit" disabled={loading || !formData.date} className="w-full bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white py-4 rounded-xl font-bold text-base md:text-lg transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="booking-submit-btn">
              {loading ? <Loader2 size={22} className="animate-spin" /> : <CreditCard size={22} />}
              {loading ? 'Processing...' : formData.package === 'Custom' ? `Build Your Party & Pay` : `Select Add-ons & Pay Rs ${getAmount().toLocaleString()}`}
            </button>
            <PaymentBadges />
          </form>
        </div>
      </div>
    </section>

    <AddOnPopup
      isOpen={showAddOns}
      onClose={() => setShowAddOns(false)}
      onConfirm={handleAddOnsConfirm}
      selectedPackage={formData.package}
      packagePrice={pkgPrice}
      eventDate={formData.date}
    />
    <LoginPromptModal show={showLoginPrompt} onClose={() => setShowLoginPrompt(false)} onLogin={handleLoginRedirect} />
    <BookingConfirmedModal booking={confirmedBooking} onClose={() => { setConfirmedBooking(null); window.location.href = '/my-account'; }} />
    </>
  );
};
